import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type EdgeId,
  type GeneratedPacketData,
  type NodeId,
  type NodeSn,
  type PathLookupNodeData,
  getRecordEntries,
  isNonNullable,
  isNullable,
  isPathLookupEdgeData,
} from '@ipfabric/utils';
import type {
  PathInspectorPacket,
  PathInspectorPackets,
  PathLookupPathInspectorSelectedPacket,
} from 'components/DeviceView/deviceViewTypes';
import { findNodeIdByNodeSn } from 'components/Graph/commands/pathLookup/helpers/findNodeIdByNodeSn';
import { isPathLookupNodeData } from 'components/Graph/helpers/isPathLookupNodeData';
import { selectPathLookupApplied } from 'components/Graph/model/selectors/graphMetadata/selectPathLookupApplied';
import { option, record } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';
import type { GraphState } from '../../GraphState';
import type { GraphData } from '../GraphData';

import { getFollowingPacketIds } from 'components/Graph/commands/pathLookup/helpers/getFollowingPacketIds';

const hasGeneratedPackets = (graph: GraphData, nodeId: NodeId) => {
  const nodeData = graph.nodes[nodeId];
  return (
    isPathLookupNodeData(nodeData) &&
    Object.keys(nodeData.generatedPackets ?? {}).length > 0
  );
};

const getEdgePackets = (
  graph: GraphData,
  highlightedEdgeIds: EdgeId[],
): PathInspectorPacket[] =>
  highlightedEdgeIds
    .map((edgeId: EdgeId): PathInspectorPacket | null => {
      const edgeData = graph.edges[edgeId];
      if (edgeData !== undefined && isPathLookupEdgeData(edgeData)) {
        return {
          edgeDirection: edgeData.direction,
          id: edgeId,
          labels: edgeData.labels,
          packet: edgeData.packet,
          severityInfo: edgeData.severityInfo,
          sourceLabel: graph.nodes[edgeData.source]?.label ?? null,
          targetLabel: graph.nodes[edgeData.target]?.label ?? null,
          trafficScore: edgeData.trafficScore,
        };
      }
      return null;
    })
    .filter(
      (packet: PathInspectorPacket | null): packet is PathInspectorPacket =>
        packet !== null,
    );

const getPathInspectorTerminatedPackets = (
  terminatedPacketIds: EdgeId[],
  nodeData: PathLookupNodeData,
  source: 'acceptedPackets' | 'droppedPackets',
) =>
  terminatedPacketIds
    .filter((packetId) => nodeData[source]?.[packetId] !== undefined)
    .map((packetId) => ({
      id: packetId,
      // Fixed noUncheckedIndexedAccess NIM-19408
      packet: nodeData[source]![packetId]!.packet,
    }));

const getPathInspectorGeneratedPackets = (
  graph: GraphData,
  nodeId: NodeId,
): PathInspectorPacket[] => {
  const nodeData = graph.nodes[nodeId];
  if (!isPathLookupNodeData(nodeData)) {
    return [];
  }

  return getRecordEntries(nodeData.generatedPackets ?? {}).map(
    ([packetId, generatedPacket]: [EdgeId, GeneratedPacketData]) => ({
      id: packetId,
      packet: generatedPacket.packet,
      severityInfo: generatedPacket.severityInfo,
      trafficScore: generatedPacket.trafficScore,
    }),
  );
};

const emptyTerminatedPackets = {
  acceptedPackets: [],
  droppedPackets: [],
};

const getGeneratedPackets = (
  graph: GraphData,
  nodeSn: NodeSn,
  nodeSelectedPackets: PathLookupPathInspectorSelectedPacket,
): PathInspectorPacket[] => {
  const nodeId = findNodeIdByNodeSn(graph, nodeSn);

  // Generated packets can be displayed only in incoming packets list
  if (
    isNonNullable(nodeSelectedPackets.selectedIncomingPacket) ||
    nodeId === null ||
    !hasGeneratedPackets(graph, nodeId)
  ) {
    return [];
  }
  return getPathInspectorGeneratedPackets(graph, nodeId);
};

const getTerminatedPackets = (
  graph: GraphData,
  nodeSn: NodeSn,
  nodeSelectedPackets: PathLookupPathInspectorSelectedPacket,
): {
  acceptedPackets: PathInspectorPacket[];
  droppedPackets: PathInspectorPacket[];
} => {
  const nodeId = findNodeIdByNodeSn(graph, nodeSn);

  if (
    nodeId === null ||
    isNullable(nodeSelectedPackets.selectedIncomingPacket)
  ) {
    return emptyTerminatedPackets;
  }
  const nodeData = graph.nodes[nodeId];
  const edgeData = graph.edges[nodeSelectedPackets.selectedIncomingPacket.id];
  const followingPacketIds = getFollowingPacketIds(
    nodeSelectedPackets.selectedIncomingPacket.id,
    edgeData,
    nodeData,
  );

  if (!isPathLookupNodeData(nodeData) || followingPacketIds === null) {
    return emptyTerminatedPackets;
  }

  const terminatedPacketIds = followingPacketIds.filter(
    (id) => graph.edges[id] === undefined,
  );

  return {
    acceptedPackets: getPathInspectorTerminatedPackets(
      terminatedPacketIds,
      nodeData,
      'acceptedPackets',
    ),
    droppedPackets: getPathInspectorTerminatedPackets(
      terminatedPacketIds,
      nodeData,
      'droppedPackets',
    ),
  };
};

export const selectPathInspectorPackets: (
  s: GraphState,
) => PathInspectorPackets | null = createScopedSelector(() =>
  createSelector(
    (s) => s.graph,
    selectPathLookupApplied,
    (s) => s.pathlookup,
    (
      graph,
      pathLookupApplied,
      { pathInspector: { selectedPackets, highlightedEdges } },
    ) => {
      if (!pathLookupApplied || Object.keys(selectedPackets).length === 0) {
        return null;
      }

      return pipe(
        selectedPackets,
        record.filterMapWithIndex((nodeSn, nodeSelectedPackets) => {
          const nodeHighlightedEdges = highlightedEdges[nodeSn];
          if (nodeHighlightedEdges === undefined) {
            stumble('Cannot select path inspector highlighted edges');
          }

          const highlightedEdgeIds: EdgeId[] =
            nodeHighlightedEdges !== undefined
              ? [
                  ...nodeHighlightedEdges.incomingEdges,
                  ...nodeHighlightedEdges.outgoingEdges,
                ]
              : [];

          const edgePackets = getEdgePackets(graph, highlightedEdgeIds);

          const { acceptedPackets, droppedPackets } = getTerminatedPackets(
            graph,
            nodeSn,
            nodeSelectedPackets,
          );

          const generatedPackets = getGeneratedPackets(
            graph,
            nodeSn,
            nodeSelectedPackets,
          );

          return option.some({
            acceptedPackets,
            droppedPackets,
            edgePackets,
            generatedPackets,
          });
        }),
      );
    },
  ),
);
